/**
 * Formats an arbitrary workflow field value as a human-readable string.
 *
 * Rules:
 *   - null/undefined     → "—"
 *   - string             → as-is (or "—" when empty)
 *   - number/boolean     → String()
 *   - primitive array    → items joined with ", "
 *   - object array       → one item per line; each item's non-empty values
 *                          joined with "  ·  " (middot separator)
 *   - plain object       → JSON pretty-printed
 *
 * Used in both the ora WorkflowDetailDrawer and the add-in InstanceDetailDrawer
 * so that both apps display the same formatted output for the same data.
 */
export function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value || "—";
  if (typeof value === "number" || typeof value === "boolean") return String(value);

  if (Array.isArray(value)) {
    if (value.length === 0) return "—";

    const firstItem = value[0];
    if (typeof firstItem === "object" && firstItem !== null) {
      // Array of objects: one line per item, values separated by middot
      return value
        .map((item) =>
          Object.values(item as Record<string, unknown>)
            .filter((v) => v != null && v !== "")
            .join("  ·  "),
        )
        .join("\n");
    }

    // Array of primitives
    return value.map(String).join(", ");
  }

  return JSON.stringify(value, null, 2);
}
